import { useState } from "react";
import { Box, TextField, Button, Typography, Stack, Avatar } from "@mui/material";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import { theme } from "../../../../../configs/theme";
import imageFileToBase64 from "../../../../../hooks/imageToBase64";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Field = ({
  title,
  description,
  value,
  onChange,
  error = "",
  type = "text",
  placeholder = "",
}) => (
  <Box sx={{ width: "100%" }}>
    <Typography
      variant="subtitle2"
      color="text.secondary"
      sx={{ mb: 0.5, fontWeight: 600 }}
    >
      {title}
    </Typography>
    {description ? (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {description}
      </Typography>
    ) : null}
    <TextField
      variant="outlined"
      fullWidth
      size="small"
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      error={Boolean(error)}
      helperText={error || undefined}
      FormHelperTextProps={error ? undefined : { sx: { display: "none" } }}
      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
    />
  </Box>
);

const AddUser = ({ addUser }) => {
  const [avatar, setAvatar] = useState(null);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [confirmEmailError, setConfirmEmailError] = useState("");
  const [error, setError] = useState("");
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    setUploadingAvatar(true);
    setError("");
    try {
      const base64 = await imageFileToBase64(file, 200);
      setAvatar(base64);
    } catch (err) {
      console.error(err);
      setError(err.message || "Unable to process image.");
    } finally {
      setUploadingAvatar(false);
    }
  };

  const validate = () => {
    const trimmedName = username.trim();
    const trimmedEmail = email.trim();
    const trimmedConfirm = confirmEmail.trim();

    let nameErr = "";
    let emailErr = "";
    let confirmErr = "";

    if (!trimmedName) {
      nameErr = "Username is required.";
    }

    if (!trimmedEmail) {
      emailErr = "Email is required.";
    } else if (!EMAIL_PATTERN.test(trimmedEmail)) {
      emailErr = "Enter a valid email address.";
    }

    if (!trimmedConfirm) {
      confirmErr = "Please confirm the email address.";
    } else if (trimmedEmail.toLowerCase() !== trimmedConfirm.toLowerCase()) {
      confirmErr = "Email addresses do not match.";
    }

    setUsernameError(nameErr);
    setEmailError(emailErr);
    setConfirmEmailError(confirmErr);
    setError("");

    return !nameErr && !emailErr && !confirmErr;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) return;

    addUser({
      id: 0,
      name: username.trim(),
      email: email.trim(),
      icon: avatar,
      job_role: "Admin",
    });
  };

  return (
    <Box>
      <Stack spacing={2.5}>
        <Box
          sx={{
            width: "100%",
            p: 2,
            borderRadius: "12px",
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "#fafafa",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Avatar
            src={avatar}
            sx={{
              width: 88,
              height: 88,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "#fff",
              color: "text.secondary",
              fontSize: 32,
              fontWeight: 600,
            }}
          >
            {(username || "U").charAt(0).toUpperCase()}
          </Avatar>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Profile avatar
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textAlign: "center", mt: -0.5 }}
          >
            Optional image shown with this user.
          </Typography>
          <Button
            variant="outlined"
            component="label"
            size="small"
            startIcon={<PhotoCameraOutlinedIcon />}
            disabled={uploadingAvatar}
            theme={theme}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
            }}
          >
            {uploadingAvatar ? "Processing..." : "Upload avatar"}
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleAvatarChange}
            />
          </Button>
        </Box>

        <Field
          title="Username"
          description="Display name shown across the portal."
          placeholder="e.g. Jane Smith"
          value={username}
          error={usernameError}
          onChange={(e) => {
            setUsername(e.target.value);
            if (usernameError && e.target.value.trim()) setUsernameError("");
          }}
        />

        <Field
          title="Email"
          description="We’ll send a confirmation email to this address."
          placeholder="name@company.com"
          type="email"
          value={email}
          error={emailError}
          onChange={(e) => {
            setEmail(e.target.value);
            if (emailError) setEmailError("");
            if (
              confirmEmailError &&
              confirmEmail.trim() &&
              e.target.value.trim().toLowerCase() ===
                confirmEmail.trim().toLowerCase()
            ) {
              setConfirmEmailError("");
            }
          }}
        />

        <Field
          title="Confirm email"
          description="Re-enter the email to make sure it’s correct."
          placeholder="name@company.com"
          type="email"
          value={confirmEmail}
          error={confirmEmailError}
          onChange={(e) => {
            setConfirmEmail(e.target.value);
            if (confirmEmailError) setConfirmEmailError("");
          }}
        />

        {error ? (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        ) : null}

        <Button
          type="button"
          onClick={handleSubmit}
          variant="contained"
          theme={theme}
          fullWidth
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "10px",
            py: 1.25,
          }}
        >
          Save user
        </Button>
      </Stack>
    </Box>
  );
};

export default AddUser;
